import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ShieldCheck, FileText, Download, AlertCircle, AlertTriangle, Clock,
  CheckCircle, XCircle, Edit, Trash2, History, User, Mail, Phone,
  Eye, RefreshCw, Shield, Plus, Building, MapPin, Smartphone
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ConsentCollectionModal from '../components/ConsentCollectionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_BASIS_OPTIONS = [
  { value: 'consent', label: 'Consentimento (Art. 6º, nº 1, a)' },
  { value: 'contract', label: 'Execução de Contrato (Art. 6º, nº 1, b)' },
  { value: 'legal_obligation', label: 'Obrigação Legal (Art. 6º, nº 1, c)' },
  { value: 'vital_interest', label: 'Interesse Vital (Art. 6º, nº 1, d)' },
  { value: 'public_interest', label: 'Interesse Público (Art. 6º, nº 1, e)' },
  { value: 'legitimate_interest', label: 'Interesse Legítimo (Art. 6º, nº 1, f)' }
];

const TitularDashboard = () => {
  const { user } = useOutletContext();
  const [consents, setConsents] = useState([]);
  const [consentPurposes, setConsentPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiringConsents, setExpiringConsents] = useState([]);
  
  // Full profile data
  const [fullProfile, setFullProfile] = useState(null);
  
  // Profile editing - all fields
  const [editProfileDialog, setEditProfileDialog] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    first_name: '',
    last_name: '',
    surname: '',
    email: '',
    phone: '',
    mobile: '',
    company_name: '',
    nif: '',
    autoline_id: '',
    address: '',
    locality: '',
    postal_code: '',
    legal_basis: ''
  });
  
  // Add consent dialog - now using ConsentCollectionModal
  const [addConsentDialog, setAddConsentDialog] = useState(false);
  const [consentModalTitular, setConsentModalTitular] = useState(null);
  
  // Revoke consent dialog
  const [revokeDialog, setRevokeDialog] = useState({ open: false, consent: null });
  const [revokeReason, setRevokeReason] = useState('');
  
  // RTBF (Right to be Forgotten) dialog
  const [rtbfDialog, setRtbfDialog] = useState(false);
  const [rtbfConfirmation, setRtbfConfirmation] = useState('');
  const [rtbfReason, setRtbfReason] = useState('');
  
  // History dialog
  const [historyDialog, setHistoryDialog] = useState({ open: false, history: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchConsents(),
        fetchConsentPurposes(),
        fetchUserProfile()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsents = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/consents`, { withCredentials: true });
      const allConsents = response.data || [];
      setConsents(allConsents);
      
      // Filter expiring consents (within 30 days)
      const now = new Date();
      const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const expiring = allConsents.filter(c => {
        if (!c.expiration_date || c.status !== 'active') return false;
        const expDate = new Date(c.expiration_date);
        return expDate > now && expDate <= thirtyDaysLater;
      });
      setExpiringConsents(expiring);
    } catch (error) {
      console.error('Error fetching consents:', error);
      toast.error('Erro ao carregar consentimentos');
    }
  };

  const fetchConsentPurposes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/consent-purposes/active`, { withCredentials: true });
      setConsentPurposes(response.data || []);
    } catch (error) {
      console.error('Error fetching purposes:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      // Get full profile data
      const response = await axios.get(`${BACKEND_URL}/api/titulares/${user.user_id}`, { withCredentials: true });
      const data = response.data;
      // Handle both { user: {...} } and direct response formats
      const profile = data.user || data;
      setFullProfile(profile);
      
      setProfileForm({
        name: profile.name || '',
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        surname: profile.surname || '',
        email: profile.email || '',
        phone: profile.phone || '',
        mobile: profile.mobile || '',
        company_name: profile.company_name || '',
        nif: profile.nif || '',
        autoline_id: profile.autoline_id || '',
        address: profile.address || '',
        locality: profile.locality || '',
        postal_code: profile.postal_code || '',
        legal_basis: profile.legal_basis || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      // Fallback to basic user info
      setProfileForm({
        name: user?.name || '',
        email: user?.email || '',
        phone: '',
        mobile: '',
        first_name: '',
        last_name: '',
        surname: '',
        company_name: '',
        nif: '',
        autoline_id: '',
        address: '',
        locality: '',
        postal_code: '',
        legal_basis: ''
      });
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dados_pessoais_${user.user_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      // Build name from parts if changed
      const updatedForm = { ...profileForm };
      if (profileForm.first_name || profileForm.last_name || profileForm.surname) {
        const nameParts = [
          profileForm.first_name,
          profileForm.last_name,
          profileForm.surname
        ].filter(Boolean);
        if (nameParts.length > 0) {
          updatedForm.name = nameParts.join(' ');
        }
      }
      
      await axios.patch(`${BACKEND_URL}/api/titulares/${user.user_id}`, updatedForm, { withCredentials: true });
      toast.success('Perfil atualizado com sucesso');
      setEditProfileDialog(false);
      fetchUserProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    }
  };

  const handleOpenAddConsentModal = () => {
    // Prepare titular data for the consent collection modal
    setConsentModalTitular({
      user_id: user.user_id,
      name: fullProfile?.name || user?.name,
      email: fullProfile?.email || user?.email,
      phone: fullProfile?.phone,
      mobile: fullProfile?.mobile,
      purposes: [] // Empty - user will select in modal
    });
    setAddConsentDialog(true);
  };

  const handleConsentSuccess = () => {
    toast.success('Consentimento adicionado com sucesso!');
    setAddConsentDialog(false);
    setConsentModalTitular(null);
    fetchConsents();
  };

  const handleRevokeConsent = async () => {
    if (!revokeDialog.consent) return;
    
    try {
      await axios.patch(
        `${BACKEND_URL}/api/consents/${revokeDialog.consent.consent_id}/revoke`,
        { reason: revokeReason },
        { withCredentials: true }
      );
      toast.success('Consentimento revogado com sucesso');
      setRevokeDialog({ open: false, consent: null });
      setRevokeReason('');
      fetchConsents();
    } catch (error) {
      console.error('Error revoking consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao revogar consentimento');
    }
  };

  const handleRTBFRequest = async () => {
    if (rtbfConfirmation !== 'APAGAR') {
      toast.error('Por favor escreva "APAGAR" para confirmar');
      return;
    }
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/rtbf/request`,
        { 
          user_id: user.user_id,
          reason: rtbfReason,
          execution_type: 'delayed',
          identity_verified: true
        },
        { withCredentials: true }
      );
      toast.success('Pedido de eliminação de dados submetido. Será processado em 30 dias.');
      setRtbfDialog(false);
      setRtbfConfirmation('');
      setRtbfReason('');
    } catch (error) {
      console.error('Error submitting RTBF:', error);
      toast.error(error.response?.data?.detail || 'Erro ao submeter pedido');
    }
  };

  const handleViewHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/audit/user/${user.user_id}`, { withCredentials: true });
      setHistoryDialog({ open: true, history: response.data || [] });
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleRenewConsent = async (consent) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/consents/${consent.consent_id}/renew`,
        {},
        { withCredentials: true }
      );
      toast.success('Consentimento renovado com sucesso');
      fetchConsents();
    } catch (error) {
      console.error('Error renewing consent:', error);
      toast.error(error.response?.data?.detail || 'Erro ao renovar consentimento');
    }
  };

  const getPurposeName = (purposeId) => {
    const purpose = consentPurposes.find(p => p.purpose_id === purposeId);
    return purpose?.name || purposeId;
  };

  const getLegalBasisLabel = (value) => {
    const option = LEGAL_BASIS_OPTIONS.find(o => o.value === value);
    return option?.label || value || 'Não definida';
  };

  // Get purposes not yet consented
  const getAvailablePurposes = () => {
    const consentedPurposes = new Set();
    consents.filter(c => c.status === 'active').forEach(c => {
      c.purposes?.forEach(p => consentedPurposes.add(p));
    });
    return consentPurposes.filter(p => !consentedPurposes.has(p.purpose_id));
  };

  const activeConsents = consents.filter(c => c.status === 'active').length;
  const revokedConsents = consents.filter(c => c.status === 'revoked').length;
  const expiredConsents = consents.filter(c => {
    if (c.status !== 'active' || !c.expiration_date) return false;
    return new Date(c.expiration_date) < new Date();
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="titular-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Portal do Titular
          </h1>
          <p className="text-slate-600">
            Bem-vindo, <span className="font-semibold">{user?.name}</span>. Gerir os seus consentimentos e dados pessoais.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenAddConsentModal} data-testid="add-consent-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Consentimento
          </Button>
          <Button variant="outline" onClick={() => setEditProfileDialog(true)} data-testid="edit-profile-btn">
            <Edit className="w-4 h-4 mr-2" />
            Editar Perfil
          </Button>
        </div>
      </div>

      {/* Expiring Consents Alert */}
      {expiringConsents.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Consentimentos a Expirar</AlertTitle>
          <AlertDescription className="text-amber-700">
            Tem {expiringConsents.length} consentimento(s) a expirar nos próximos 30 dias. 
            Renove-os para continuar a receber comunicações.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Consentimentos Ativos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-[#10B981]">{activeConsents}</div>
              <ShieldCheck className="w-8 h-8 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">A Expirar (30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-amber-600">{expiringConsents.length}</div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Revogados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-slate-600">{revokedConsents}</div>
              <XCircle className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-[#0F172A]">{consents.length}</div>
              <FileText className="w-8 h-8 text-[#3B82F6]" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="consents" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="consents" className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Os Meus Consentimentos
          </TabsTrigger>
          <TabsTrigger value="rights" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Os Meus Direitos
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Os Meus Dados
          </TabsTrigger>
        </TabsList>

        {/* Consents Tab */}
        <TabsContent value="consents">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#10B981]" />
                    Gerir Consentimentos
                  </CardTitle>
                  <CardDescription>
                    Visualize, renove ou revogue os seus consentimentos de tratamento de dados
                  </CardDescription>
                </div>
                {getAvailablePurposes().length > 0 && (
                  <Button onClick={handleOpenAddConsentModal} className="bg-[#10B981] hover:bg-[#10B981]/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {consents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Não tem consentimentos registados</p>
                  <Button onClick={handleOpenAddConsentModal} className="mt-4 bg-[#10B981] hover:bg-[#10B981]/90">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Primeiro Consentimento
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Finalidade</TableHead>
                      <TableHead>Canal</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Expira</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consents.map((consent) => {
                      const isExpired = consent.expiration_date && new Date(consent.expiration_date) < new Date();
                      const isExpiringSoon = consent.expiration_date && 
                        new Date(consent.expiration_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) &&
                        new Date(consent.expiration_date) > new Date();
                      
                      return (
                        <TableRow 
                          key={consent.consent_id} 
                          className={isExpired ? 'bg-red-50' : isExpiringSoon ? 'bg-amber-50' : ''}
                          data-testid={`consent-row-${consent.consent_id}`}
                        >
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {consent.purposes?.map(p => (
                                <Badge key={p} variant="outline" className="text-xs">
                                  {getPurposeName(p)}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {consent.contact_type === 'email' && <Mail className="w-4 h-4 text-slate-500" />}
                              {consent.contact_type === 'phone' && <Phone className="w-4 h-4 text-slate-500" />}
                              {consent.contact_type === 'mobile' && <Smartphone className="w-4 h-4 text-slate-500" />}
                              <span className="text-sm">{consent.contact_value}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {consent.status === 'active' && !isExpired ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Ativo
                              </Badge>
                            ) : isExpired ? (
                              <Badge className="bg-red-50 text-red-700 border-red-200">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Expirado
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                                <XCircle className="w-3 h-3 mr-1" />
                                Revogado
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {consent.created_at ? new Date(consent.created_at).toLocaleDateString('pt-PT') : '-'}
                          </TableCell>
                          <TableCell>
                            {consent.expiration_date ? (
                              <span className={`text-sm ${isExpired ? 'text-red-600 font-medium' : isExpiringSoon ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>
                                {new Date(consent.expiration_date).toLocaleDateString('pt-PT')}
                                {isExpiringSoon && (
                                  <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">
                                    A expirar
                                  </Badge>
                                )}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {consent.status === 'active' && isExpiringSoon && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRenewConsent(consent)}
                                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                  data-testid={`renew-consent-${consent.consent_id}`}
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Renovar
                                </Button>
                              )}
                              {consent.status === 'active' && !isExpired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setRevokeDialog({ open: true, consent })}
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  data-testid={`revoke-consent-${consent.consent_id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Revogar
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rights Tab */}
        <TabsContent value="rights">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Os Seus Direitos RGPD</CardTitle>
              <CardDescription>
                Conforme o Regulamento Geral sobre a Proteção de Dados, tem os seguintes direitos:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-500" />
                    Direito de Acesso (Art. 15º)
                  </h3>
                  <p className="text-sm text-slate-600">Pode solicitar acesso aos seus dados pessoais.</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                    <Edit className="w-4 h-4 text-green-500" />
                    Direito de Retificação (Art. 16º)
                  </h3>
                  <p className="text-sm text-slate-600">Pode corrigir dados pessoais incorretos.</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                    <Trash2 className="w-4 h-4 text-red-500" />
                    Direito ao Apagamento (Art. 17º)
                  </h3>
                  <p className="text-sm text-slate-600">Pode solicitar a eliminação dos seus dados.</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-[#0F172A] mb-2 flex items-center gap-2">
                    <Download className="w-4 h-4 text-purple-500" />
                    Direito à Portabilidade (Art. 20º)
                  </h3>
                  <p className="text-sm text-slate-600">Pode receber os seus dados em formato estruturado.</p>
                </div>
              </div>

              {/* RTBF */}
              <div className="border-t pt-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Trash2 className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-800 mb-1">Direito ao Esquecimento (Art. 17º RGPD)</h3>
                      <p className="text-sm text-red-700 mb-3">
                        Pode solicitar a eliminação completa dos seus dados pessoais da nossa plataforma. 
                        Este processo é irreversível e será executado em 30 dias.
                      </p>
                      <Button 
                        variant="outline" 
                        className="border-red-300 text-red-700 hover:bg-red-100"
                        onClick={() => setRtbfDialog(true)}
                        data-testid="rtbf-request-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Solicitar Eliminação de Dados
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Tab */}
        <TabsContent value="data">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#3B82F6]" />
                Os Meus Dados Pessoais
              </CardTitle>
              <CardDescription>
                Informações pessoais armazenadas no sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Identificação Pessoal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Primeiro Nome</Label>
                    <p className="font-medium">{profileForm.first_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Último Nome</Label>
                    <p className="font-medium">{profileForm.last_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Apelido</Label>
                    <p className="font-medium">{profileForm.surname || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Contactos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p className="font-medium">{profileForm.email || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Telefone</Label>
                    <p className="font-medium">{profileForm.phone || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Telemóvel</Label>
                    <p className="font-medium">{profileForm.mobile || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Dados Empresariais
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Empresa</Label>
                    <p className="font-medium">{profileForm.company_name || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">NIF</Label>
                    <p className="font-medium">{profileForm.nif || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">ID Autoline</Label>
                    <p className="font-medium">{profileForm.autoline_id || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Morada
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Morada</Label>
                    <p className="font-medium">{profileForm.address || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Localidade</Label>
                    <p className="font-medium">{profileForm.locality || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500">Código Postal</Label>
                    <p className="font-medium">{profileForm.postal_code || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Legal Basis */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Base Legal
                </h3>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="text-xs text-slate-500">Base Legal de Tratamento</Label>
                  <p className="font-medium">{getLegalBasisLabel(profileForm.legal_basis)}</p>
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t">
                <Button onClick={handleExportPDF} className="bg-[#0F172A] hover:bg-[#0F172A]/90" data-testid="export-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Exportar Ficha de Dados (PDF)
                </Button>
                <Button variant="outline" onClick={handleViewHistory} data-testid="view-history-btn">
                  <History className="w-4 h-4 mr-2" />
                  Ver Histórico de Alterações
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Profile Dialog - Full Fields */}
      <Dialog open={editProfileDialog} onOpenChange={setEditProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#3B82F6]" />
              Editar Perfil
            </DialogTitle>
            <DialogDescription>Atualizar os seus dados pessoais</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Personal Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Identificação Pessoal</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-first-name">Primeiro Nome</Label>
                    <Input
                      id="edit-first-name"
                      value={profileForm.first_name}
                      onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-last-name">Último Nome</Label>
                    <Input
                      id="edit-last-name"
                      value={profileForm.last_name}
                      onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-surname">Apelido</Label>
                    <Input
                      id="edit-surname"
                      value={profileForm.surname}
                      onChange={(e) => setProfileForm({ ...profileForm, surname: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Contactos</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-email">Email</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={profileForm.email}
                      disabled
                    />
                    <p className="text-xs text-slate-500 mt-1">Email não pode ser alterado</p>
                  </div>
                  <div>
                    <Label htmlFor="edit-phone">Telefone</Label>
                    <Input
                      id="edit-phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+351 21 000 0000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-mobile">Telemóvel</Label>
                    <Input
                      id="edit-mobile"
                      value={profileForm.mobile}
                      onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value })}
                      placeholder="+351 912 345 678"
                    />
                  </div>
                </div>
              </div>

              {/* Company Info */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Dados Empresariais</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-company">Empresa</Label>
                    <Input
                      id="edit-company"
                      value={profileForm.company_name}
                      onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-nif">NIF</Label>
                    <Input
                      id="edit-nif"
                      value={profileForm.nif}
                      onChange={(e) => setProfileForm({ ...profileForm, nif: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-autoline">ID Autoline</Label>
                    <Input
                      id="edit-autoline"
                      value={profileForm.autoline_id}
                      onChange={(e) => setProfileForm({ ...profileForm, autoline_id: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Morada</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-address">Morada</Label>
                    <Input
                      id="edit-address"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-locality">Localidade</Label>
                    <Input
                      id="edit-locality"
                      value={profileForm.locality}
                      onChange={(e) => setProfileForm({ ...profileForm, locality: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-postal">Código Postal</Label>
                    <Input
                      id="edit-postal"
                      value={profileForm.postal_code}
                      onChange={(e) => setProfileForm({ ...profileForm, postal_code: e.target.value })}
                      placeholder="0000-000"
                    />
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdateProfile} className="bg-[#10B981] hover:bg-[#10B981]/90">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Consent Modal - uses QR Code or OTP validation */}
      <ConsentCollectionModal
        isOpen={addConsentDialog}
        onClose={() => {
          setAddConsentDialog(false);
          setConsentModalTitular(null);
        }}
        onSuccess={handleConsentSuccess}
        titularData={consentModalTitular}
        showForm={true}
      />

      {/* Revoke Consent Dialog */}
      <Dialog open={revokeDialog.open} onOpenChange={(open) => setRevokeDialog({ open, consent: open ? revokeDialog.consent : null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Revogar Consentimento
            </DialogTitle>
            <DialogDescription>
              Esta ação irá revogar o seu consentimento. Deixará de receber comunicações relacionadas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <strong>Finalidades:</strong> {revokeDialog.consent?.purposes?.map(p => getPurposeName(p)).join(', ')}
              </p>
              <p className="text-sm text-amber-700 mt-1">
                <strong>Canal:</strong> {revokeDialog.consent?.contact_value}
              </p>
            </div>
            <div>
              <Label htmlFor="revoke-reason">Motivo da revogação (opcional)</Label>
              <Input
                id="revoke-reason"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value)}
                placeholder="Ex: Já não desejo receber comunicações"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeDialog({ open: false, consent: null })}>Cancelar</Button>
            <Button onClick={handleRevokeConsent} className="bg-red-600 hover:bg-red-700 text-white">
              Confirmar Revogação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RTBF Dialog */}
      <Dialog open={rtbfDialog} onOpenChange={setRtbfDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Direito ao Esquecimento (Art. 17º RGPD)
            </DialogTitle>
            <DialogDescription>Solicitar a eliminação completa dos seus dados pessoais</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Alert className="border-red-300 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800">Atenção - Ação Irreversível</AlertTitle>
              <AlertDescription className="text-red-700">
                Esta ação irá eliminar permanentemente todos os seus dados pessoais.
              </AlertDescription>
            </Alert>
            
            <div>
              <Label htmlFor="rtbf-reason">Motivo do pedido</Label>
              <Input
                id="rtbf-reason"
                value={rtbfReason}
                onChange={(e) => setRtbfReason(e.target.value)}
                placeholder="Descreva o motivo do seu pedido"
              />
            </div>
            
            <div>
              <Label htmlFor="rtbf-confirm" className="text-red-600">
                Para confirmar, escreva "APAGAR" no campo abaixo:
              </Label>
              <Input
                id="rtbf-confirm"
                value={rtbfConfirmation}
                onChange={(e) => setRtbfConfirmation(e.target.value.toUpperCase())}
                placeholder="APAGAR"
                className="border-red-200 focus:border-red-400"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRtbfDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleRTBFRequest} 
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={rtbfConfirmation !== 'APAGAR'}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Confirmar Eliminação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => setHistoryDialog({ open, history: open ? historyDialog.history : [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#3B82F6]" />
              Histórico de Alterações
            </DialogTitle>
            <DialogDescription>Registo de todas as alterações aos seus dados</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {historyDialog.history.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Sem histórico de alterações</p>
              </div>
            ) : (
              historyDialog.history.map((entry, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">{entry.action_type || entry.action}</Badge>
                    <span className="text-xs text-slate-500">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleString('pt-PT') : '-'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{entry.description || entry.details}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TitularDashboard;
